import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/cn';

/**
 * Button — three variants only (primary, secondary, ghost) plus danger,
 * per DESIGN_SYSTEM §3. No outline buttons. 36 px height matches Input.
 *
 * Hierarchy comes from hue *and* weight: the brand assigns Blue to primary
 * actions, so solid Bigx Blue leads, a blue wash supports, and ghost recedes
 * to neutral text. Danger is solid red — the brand assigns Red to delete and
 * overdue, and those should be recognisable before the label is read.
 *
 * The solid weights are built on `--accent-ink` / `--danger-ink`, not on the
 * bright brand colours: white text on #0078D9 is 4.48:1, just under AA. See
 * theme.css for the full note.
 *
 * DESIGN_SYSTEM §2.5 bans button shadows; the theme reinstates a tight one on
 * the solid weights, because a solid button with no shadow looks pasted onto
 * the card rather than raised above it. It is a lift cue, not decoration.
 *
 * Borders, never `ring-*`: Tailwind's ring is implemented as box-shadow and
 * would replace the material's inset sheen rather than sit alongside it.
 */
const button = cva(
  'inline-flex items-center justify-center gap-2 rounded-md font-medium ' +
    'border transition-colors duration-120 ease-apple ' +
    'disabled:opacity-40 disabled:cursor-not-allowed ' +
    'focus-visible:outline-none',
  {
    variants: {
      variant: {
        primary:   'btn-brand border-transparent',
        // The soft weight carries its own inset ring, so no border colour.
        secondary: 'btn-brand-soft border-transparent',
        ghost:     'border-transparent text-ink-700 hover:bg-ink-500/10',
        danger:    'btn-danger border-transparent',
      },
      size: {
        sm: 'h-8  px-3 text-body',
        md: 'h-9  px-4 text-body',
        lg: 'h-11 px-5 text-body',
      },
      block: { true: 'w-full' },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, block, ...props }, ref) => (
    <button ref={ref} className={cn(button({ variant, size, block }), className)} {...props} />
  ),
);
Button.displayName = 'Button';
